// Register our arrays of components by a type key… eg.
// { ComponentKeyA: [],ComponentKeyB: [] }
import {addRegistryToGlobalCollection} from "./global_components";

// This is the key for the standard `ComponentRegistry` instance that is created and used by Rankers components.
// Unless there is a substantial reason to create another `ComponentRegistry` instance, use this one.
// The key is used as a property on the `window` object (see `ComponentRegistry.getForKey`).
// Therefore it should be a valid property name and should not conflict with any other property on the `window` object.
// Note that some HTML has this key hard-coded in it (eg. `window.RankersComponents`) so this key should not be changed without careful consideration.
export const defaultRegistryKey = 'RankersComponents';

export class ComponentRegistry {

    static getForKey(key) {
        if (!window[key]) window[key] = new ComponentRegistry(key);
        return window[key];
    }

    static getDefault() {
        return ComponentRegistry.getForKey(defaultRegistryKey);
    }

    constructor(identifier = null) {
        this.identifier = identifier;
        this.registeredTypeKeys = []; // An array of component type keys that have been registered (regardless of if there are components of this type present).
        this.components = {};

        addRegistryToGlobalCollection(this);
    }

    getComponentForType(componentTypeKey) {
        return this.getComponentsForType(componentTypeKey)[0];
    }

    getComponentsForType(componentTypeKey) {
        if (!this.hasComponentType(componentTypeKey)) {
            return []
        }
        return this.components[componentTypeKey];
    }

    pushComponent(componentTypeKey, instance) {
        if (!instance) {
            return
        }
        if (!this.hasComponentType(componentTypeKey)) {
            this.addComponentType(componentTypeKey)
        }
        this.components[componentTypeKey].push(instance);
    }

    // Deregister a component instance. Without this the registry is a permanent retainer:
    // every loader-constructed instance stays reachable from `window.RankersComponents` for
    // the life of the page, even once its element has been discarded.
    // Called by `Component#destroy` — prefer destroying the component over calling this.
    removeComponent(componentTypeKey, instance) {
        if (!this.hasComponentType(componentTypeKey)) {
            return
        }
        const components = this.components[componentTypeKey];
        const index = components.indexOf(instance);
        if (index !== -1) components.splice(index, 1);
    }

    addComponentType(componentTypeKey) {
        this.components[componentTypeKey] = [];

        // Dynamically add a method named for the key to access a singleton component.
        // For example `window.RankersComponents.getModalbox()` will return the single Modalbox instance.
        this['get' + componentTypeKey] = () => {
            let components = this.components[componentTypeKey];
            if (components.length === 1) {
                return components[0];
            } else {
                console.warn('Not a singleton component: ', components);
            }
        };

        // Dynamically add a method named for the key to access an array of components.
        // For example `window.RankersComponents.getAllButton()` will return all instances of Button component.
        this['getAll' + componentTypeKey] = () => {
            return this.components[componentTypeKey];
        };

    }

    hasComponentType(componentTypeKey) {
        return this.components.hasOwnProperty(componentTypeKey);
    }

    registerTypeKey(componentTypeKey) {
        if (!componentTypeKey) return;
        if (this.registeredTypeKeys.includes(componentTypeKey)) {
            console.warn("[ComponentRegistry] Attempted to register the component type key '" + componentTypeKey + "'  that has already been added on this registry:", this);
            return
        }
        this.registeredTypeKeys.push(componentTypeKey);
    }

}

